import React from 'react';
import Autosuggest, {GetSuggestionValue, InputProps} from 'react-autosuggest';

import ContractHoursToggle from '../../../../common/ContractHoursToggle';
import Preset from '../../../../common/Preset';
import ApiHelper from '../../../../helpers/apiHelper';
import DateHelper from '../../../../helpers/dateHelper';
import EmployeeSortHelper from '../../../../helpers/employeeSortHelper';
import PermissionHelper from '../../../../helpers/permissionHelper';
import StringHelper from '../../../../helpers/stringHelper';
import { onlyUnique } from '../../../../helpers/util';
import WildCardHelper from '../../../../helpers/wildCardHelper';
import { PermissionTypes } from '../../../../types/permissionTypes';
import {Action} from '../../../../typescript/actionTypes';
import {SuggestionInfo} from '../../../../typescript/infoTypes';
import {TimesheetsWeeklyReportType} from '../../../../typescript/reportTypes';
import {
    StoreSettings,
    StoreSupplier,
    StoreTsCodes,
    StoreUser
} from '../../../../typescript/storeTypes';
import WeekPicker from '../../common/WeekPicker';
import AutoSuggestList from './AutoSuggestList';

class TimesheetsWeeklyReport extends React.Component<Props, State> {
    state: State = {
        userFilter: '',
        supplierFilter: '',
        userSelection: [],
        tsCodeSelection: [],
        supplierSelection: [],
        tsCodeSuggestions: [],
        selectedWeek: 0,
        selectedDate: new Date(),
        selectedTemplateSupplierValue: '',
        selectedTemplateSupplier: null,
    };

    handleDateChange = (selectedDate: Date|undefined): void => {
        if(selectedDate === undefined) {
            this.setState({selectedWeek: 0, selectedDate: DateHelper.resetTime(new Date())});
        } else {
            this.setState({selectedWeek: DateHelper.getWeekNumber(new Date(selectedDate)), selectedDate: new Date(selectedDate)});
        }
    };

    selectionsEmpty = (): boolean => this.state.supplierSelection.length === 0 && this.state.tsCodeSelection.length === 0 && this.state.userSelection.length === 0;

    exportPdf = (): void => {
        if(!this.canExport()) return;

        const data = this.getExportData();
        this.props.exportWeeklyTimesheetsPdf(data);
    };

    exportExcel = (): void => {
        if(!this.canExport()) return;

        const data = this.getExportData();
        this.props.exportWeeklyTimesheetsExcel(data);
    };

    canExport = (): boolean => {
        if (!this.state.selectedWeek) return false;
        if (this.props.reportRequested) return false;
        if (this.selectionsEmpty()) return false;

        return true;
    };

    getExportData = (): TimesheetsWeeklyReportType => {
        let templateId = null;
        if (this.state.selectedTemplateSupplier !== null && this.state.selectedTemplateSupplier !== undefined) {
            templateId = this.state.selectedTemplateSupplier.id;
        }
        return {
            startDate: DateHelper.getMonday(this.state.selectedDate),
            companyIds: this.state.supplierSelection.map(s => s.id),
            tsCodeIds: this.state.tsCodeSelection.map(t => t.id).filter(onlyUnique),
            userIds: this.state.userSelection.map(u => u.id),
            hoursOnContract: this.props.settings.defaultContractHours,
            templateId: templateId
        };
    };

    fetchUsers = (value: string) => this.setState({userFilter: value});
    fetchSuppliers = (value: string) => this.setState({supplierFilter: value});
    fetchTsCodes: (value: string, page?: number) => void = ApiHelper.debounce((value: string, page?: number) => this.props.getTsCodes(value, page as number, true, true, this.state.selectedDate.getMonth(), this.state.selectedDate.getFullYear()), 500);

    getUserSuggestions = (): SuggestionInfo<string>[] => {
        let users = this.props.user.visibleUsers.slice();
        if (this.state.userFilter && this.state.userFilter !== '') {
            users = users.filter(vu => EmployeeSortHelper.isHit(this.state.userFilter, vu));
        }

        const list: SuggestionInfo<string>[] = [];
        for (const user of users) {
            if(list.length > 0 && list.some(e=>e.id === user.id)) continue;
            list.push({
                id: user.id,
                value: `${StringHelper.getEmployeeFullName(user, this.props.settings.firstNameLastName, false, true)}`,
                displayValue: `${StringHelper.getEmployeeFullName(user, this.props.settings.firstNameLastName, false, true)}`,
                currentCount: 0,
                totalCount:0
            });
        }

        return list;
    };

    getSupplierSuggestions = (): SuggestionInfo<string>[] => {
        let suppliers = this.props.user.visibleSuppliers.slice();
        if(this.state.supplierFilter && this.state.supplierFilter !== '') {
            suppliers = suppliers.filter(vs => WildCardHelper.containsString(this.state.supplierFilter, vs.name));
        }

        const list: SuggestionInfo<string>[] = [];
        for (const supplier of suppliers) {
            if(list.length > 0 && list.some(e=>e.id === supplier.id)) continue;
            list.push({
                id: supplier.id,
                value: supplier.name,
                displayValue: supplier.name,
                currentCount: 0,
                totalCount: 0
            });
        }

        return list;
    };

    clearTsCodePagingList = (): void => {
        this.props.clearTsCodes();
    };

    getTsCodeSuggestions = (): SuggestionInfo<string>[] => {
        return this.state.tsCodeSuggestions.slice();
    };

    addUser = (users: any[]): void => {
        const newList = this.state.userSelection.slice();

        users.forEach(u => {
            if(newList.some(user => user.id === u.id)) {
                return;
            }
            newList.push(u);
        });

        this.setState({userSelection: newList});
    };

    addTsCode = (tsCodes: any[]): void => {
        const newList = this.state.tsCodeSelection.slice();

        tsCodes.forEach(t => {
            if(newList.some(tscode => tscode.id === t.id)) {
                return;
            }
            newList.push(t);
        });

        this.setState({tsCodeSelection: newList});
    };

    addCompany = (suppliers: any[]): void => {
        const newList = this.state.supplierSelection.slice();

        suppliers.forEach(s => {
            if(newList.some(supplier => supplier.id === s.id)) {
                return;
            }
            newList.push(s);
        });

        this.setState({supplierSelection: newList});
    };

    removeUser = (ids: number[]): void => {
        const newSelection = this.state.userSelection.filter(id => ids.indexOf(id.id) === -1);
        this.setState({userSelection: newSelection});
    };

    removeTsCode = (ids: string[]): void => {
        const newSelection = this.state.tsCodeSelection.filter(id => ids.indexOf(id.id) === -1);
        this.setState({tsCodeSelection: newSelection});
    };

    removeCompany = (ids: number[]): void => {
        const newSelection = this.state.supplierSelection.filter(id => ids.indexOf(id.id) === -1);
        this.setState({supplierSelection: newSelection});
    };

    getSuppliers: (value: string) => void = ApiHelper.debounce((value: string) => this.props.getSuppliers(value), 500);

    onChange = (event: React.SyntheticEvent<HTMLElement>, {newValue}: { newValue: StoreSupplier | string }) => {
        if (newValue instanceof Object) {
            this.setState({selectedTemplateSupplierValue: newValue.name, selectedTemplateSupplier: newValue});
        } else {
            const target = event.currentTarget as HTMLInputElement;
            this.setState({
                selectedTemplateSupplierValue: target.value,
                selectedTemplateSupplier: null
            });
        }
    };

    getDataForPreset = (): any => {
        return {
            userSelection: this.state.userSelection,
            tsCodeSelection: this.state.tsCodeSelection,
            supplierSelection: this.state.supplierSelection
        };
    };

    onPresetChange = (data: any) => {
        this.setState(data);
    };

    onSuggestionsFetchRequested = ({value}: { value: string }) => this.getSuppliers(value);
    onSuggestionsClearRequested = this.props.clearSuppliers;
    getSuggestionValue: GetSuggestionValue<StoreSupplier> = (supplier: StoreSupplier): any => supplier;
    renderSuggestion = (supplier: StoreSupplier) => <div>{supplier.name}</div>;
    onBlur = (templateSupplier: StoreSupplier) => {
        if (this.state.selectedTemplateSupplierValue !== '' && this.state.selectedTemplateSupplier === null && templateSupplier === null) {
            this.setState({selectedTemplateSupplierValue: ''});
            return;
        }

        if (templateSupplier !== null) {
            this.setState({
                selectedTemplateSupplier: templateSupplier,
                selectedTemplateSupplierValue: templateSupplier.name
            });
        }
    };

    componentDidUpdate(prevProps: Props): void {
        if(this.props.user.permissions !== prevProps.user.permissions){
            if(PermissionHelper.hasPermission(this.props.user, PermissionTypes.GET_VISIBLE_SUPPLIERS) && this.props.user.visibleSuppliers.length < 1) {
                this.props.getVisibleSuppliers();
            }
        }
    }

    componentDidMount() {
        if(PermissionHelper.hasPermission(this.props.user, PermissionTypes.GET_VISIBLE_SUPPLIERS) && this.props.user.visibleSuppliers.length < 1){
            this.props.getVisibleSuppliers();
        }
    }

    static getDerivedStateFromProps(nextProps: Props, state: State) {
        if(nextProps.tsCodes.length === 0) return {tsCodeSuggestions: []};

        const list = state.tsCodeSuggestions.slice();
        for (const tsCode of nextProps.tsCodes) {
            if(list.length > 0 && list.some(e => e.id === tsCode.id && e.value === tsCode.name)) continue;
            list.push({
                id: tsCode.id,
                value: tsCode.name,
                displayValue: tsCode.name,
                currentCount: tsCode.currentCount,
                totalCount: tsCode.totalCount
            });
        }

        return {tsCodeSuggestions: list};
    }

    render() {
        const inputProps: InputProps<StoreSupplier> = {
            placeholder: '',
            value: this.state.selectedTemplateSupplierValue,
            onChange: (event: React.FormEvent<HTMLElement> & { currentTarget: HTMLElement }, newValue: { newValue: any }) => this.onChange(event, newValue),
            onBlur: (_: React.FocusEvent<HTMLElement>, suggestion : Autosuggest.BlurEvent<StoreSupplier>|undefined) => this.onBlur(suggestion?.highlightedSuggestion as StoreSupplier)
        };

        return (
            <div className="timesheets-weekly-report">
                <div className="timesheets-weekly-information">
                    <div className="info-container">
                        <label>Selected week</label>
                        <WeekPicker
                            placeholder="Pick a day in a week you want"
                            onHandleChange={this.handleDateChange}
                        />
                    </div>
                    <div className="info-container">
                        <label>Company template</label>
                        <Autosuggest suggestions={this.props.suppliers}
                                     onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                                     onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                                     getSuggestionValue={this.getSuggestionValue}
                                     renderSuggestion={this.renderSuggestion}
                                     inputProps={inputProps}
                                     highlightFirstSuggestion />
                    </div>
                    <div className="info-container">
                        <label>Hours per week</label>
                        <ContractHoursToggle />
                    </div>
                    <div className="timesheets-weekly-buttons">
                        <input type="submit" className="export-button" disabled={!this.canExport()} onClick={this.exportPdf} value="Export as PDF"/>
                        <input type="submit" className="export-button" disabled={!this.canExport()} onClick={this.exportExcel} value="Export as Xlsx"/>
                    </div>
                    {this.props.reportRequested ?
                    <div className="export-spinner">
                        <i className="fas fa-spinner fa-spin" />
                    </div> : undefined}
                </div>
                <div className="timesheets-weekly-selection-container">
                    <Preset location="timesheetsweeklyreport"
                            presetData={this.getDataForPreset()}
                            onPresetChange={this.onPresetChange}
                            displayProperties={[
                                { property:'userSelection', displayName:'Users'},
                                { property:'tsCodeSelection', displayName:'Timesheet Codes'},
                                { property:'supplierSelection', displayName:'Suppliers'}
                            ]}
                            defaultEmptyPreset={{location: 'actualsreport', name: '(empty)', data: {userSelection: [], tsCodeSelection: [], supplierSelection: []}}}
                            />
                    <div className="timesheets-weekly-selection">
                        <div className="timesheets-weekly-selection-box">
                            <AutoSuggestList<string> listName="Users"
                                            selected={this.state.userSelection}
                                            suggestions={this.getUserSuggestions()}
                                            fetch={this.fetchUsers}
                                            selectSuggestion={this.addUser}
                                            unSelectSuggestion={this.removeUser}/>
                        </div>
                        <div className="timesheets-weekly-selection-box">
                            <AutoSuggestList<string> listName="Timesheet Codes"
                                            selected={this.state.tsCodeSelection}
                                            suggestions={this.getTsCodeSuggestions()}
                                            fetch={this.fetchTsCodes}
                                            selectSuggestion={this.addTsCode}
                                            unSelectSuggestion={this.removeTsCode}
                                            clearPagingList={this.clearTsCodePagingList}
                                            pagingAllowedOnFetch/>
                        </div>
                        <div className="timesheets-weekly-selection-box">
                            <AutoSuggestList<string> listName="Companies"
                                            selected={this.state.supplierSelection}
                                            suggestions={this.getSupplierSuggestions()}
                                            fetch={this.fetchSuppliers}
                                            selectSuggestion={this.addCompany}
                                            unSelectSuggestion={this.removeCompany}/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

type Props = {
    user: StoreUser,
    suppliers: StoreSupplier[],
    tsCodes: StoreTsCodes,
    reportRequested: boolean,
    settings: StoreSettings,

    getSuppliers: (search: string) => Action,
    clearSuppliers: () => Action,
    clearTsCodes: () => Action,
    getTsCodes: (search: string, page: number, showOldAndReadOnly: boolean, showAllAliases: boolean, month: number, year: number) => Action,
    exportWeeklyTimesheetsPdf: (timesheetsWeeklyReportType: TimesheetsWeeklyReportType) => Action,
    exportWeeklyTimesheetsExcel: (timesheetsWeeklyReportType: TimesheetsWeeklyReportType) => Action,
    getVisibleSuppliers: () => Action
};

type State = {
    userFilter: string,
    supplierFilter: string,
    userSelection: SuggestionInfo<string>[],
    tsCodeSelection: SuggestionInfo<string>[],
    supplierSelection: SuggestionInfo<string>[],
    tsCodeSuggestions: SuggestionInfo<string>[],
    selectedTemplateSupplierValue: string,
    selectedTemplateSupplier: StoreSupplier | null | undefined,
    selectedWeek: number,
    selectedDate: Date
};

export default TimesheetsWeeklyReport;
